class PredictiveSearch extends SearchForm {
  constructor() {
    super();

    this.cachedResults = Object.create(null);

    this.predictiveSearchResults = this.querySelector(
      '[data-predictive-search]'
    );

    this.isOpen = false;

    this.abortController = new AbortController();

    this.searchTerm = '';

    this.requestId = 0;

    this.maxCacheEntries = 40;

    this.liveRegionTimeout = null;

    this.setupEventListeners();
  }

  setupEventListeners() {
    if (this.input?.form) {
      this.input.form.addEventListener(
        'submit',
        this.onFormSubmit.bind(this)
      );
    }

    if (this.input) {
      this.input.addEventListener(
        'focus',
        this.onFocus.bind(this)
      );
    }

    this.addEventListener(
      'focusout',
      this.onFocusOut.bind(this)
    );

    this.addEventListener(
      'keyup',
      this.onKeyup.bind(this)
    );

    this.addEventListener(
      'keydown',
      this.onKeydown.bind(this)
    );
  }

  getQuery() {
    return this.input?.value?.trim() || '';
  }

  onChange() {
    super.onChange();

    const newSearchTerm = this.getQuery();

    if (
      !this.searchTerm ||
      !newSearchTerm.startsWith(this.searchTerm)
    ) {
      /*
       * Previous results are no longer relevant.
       */
      this.querySelector(
        '#predictive-search-results-groups-wrapper'
      )?.remove();
    }

    this.updateSearchForTerm(
      this.searchTerm,
      newSearchTerm
    );

    this.searchTerm = newSearchTerm;

    /*
     * Important:
     * If the user clears the input while a request
     * is still running, cancel that request.
     */
    if (!this.searchTerm.length) {
      this.abortController.abort();

      this.abortController =
        new AbortController();

      this.requestId += 1;

      this.close(true);

      return;
    }

    this.getSearchResults(
      this.searchTerm
    );
  }

  onFormSubmit(event) {
    const selectedLink = this.querySelector(
      '[aria-selected="true"] a'
    );

    if (
      !this.getQuery().length ||
      selectedLink
    ) {
      event.preventDefault();
    }
  }

  onFormReset(event) {
    super.onFormReset(event);

    if (super.shouldResetForm()) {
      this.searchTerm = '';

      this.abortController.abort();

      this.abortController =
        new AbortController();

      this.requestId += 1;

      this.closeResults(true);
    }
  }

  onFocus() {
    const currentSearchTerm =
      this.getQuery();

    if (!currentSearchTerm.length) {
      return;
    }

    if (
      this.searchTerm !==
      currentSearchTerm
    ) {
      /*
       * Search term may have been changed from
       * another search input.
       */
      this.onChange();

      return;
    }

    if (
      this.getAttribute('results') ===
      'true'
    ) {
      this.open();

      return;
    }

    this.getSearchResults(
      this.searchTerm
    );
  }

  onFocusOut() {
    setTimeout(() => {
      if (
        !this.contains(
          document.activeElement
        )
      ) {
        this.close();
      }
    });
  }

  onKeyup(event) {
    if (!this.getQuery().length) {
      this.close(true);
    }

    switch (event.code) {
      case 'ArrowUp':
        event.preventDefault();

        this.switchOption('up');

        break;

      case 'ArrowDown':
        event.preventDefault();

        this.switchOption('down');

        break;

      case 'Enter':
        this.selectOption();

        break;
    }
  }

  onKeydown(event) {
    /*
     * Prevent the cursor from moving inside
     * the search input while navigating results.
     */
    if (
      event.code === 'ArrowUp' ||
      event.code === 'ArrowDown'
    ) {
      event.preventDefault();
    }
  }

  updateSearchForTerm(
    previousTerm,
    newTerm
  ) {
    if (
      !previousTerm ||
      previousTerm === newTerm
    ) {
      return;
    }

    const searchForTextElement =
      this.querySelector(
        '[data-predictive-search-search-for-text]'
      );

    const currentButtonText =
      searchForTextElement?.innerText;

    if (
      !searchForTextElement ||
      !currentButtonText
    ) {
      return;
    }

    /*
     * Do NOT use RegExp here.
     *
     * User searches can contain regex characters
     * such as:
     *
     * (
     * [
     * +
     * *
     * ?
     *
     * which could previously cause a JS error.
     */
    const firstMatch =
      currentButtonText.indexOf(
        previousTerm
      );

    if (firstMatch === -1) {
      return;
    }

    const secondMatch =
      currentButtonText.indexOf(
        previousTerm,
        firstMatch +
          previousTerm.length
      );

    /*
     * If the term exists more than once,
     * avoid replacing the wrong occurrence.
     */
    if (secondMatch !== -1) {
      return;
    }

    searchForTextElement.innerText =
      currentButtonText.replace(
        previousTerm,
        newTerm
      );
  }

  switchOption(direction) {
    if (!this.getAttribute('open')) {
      return;
    }

    const moveUp =
      direction === 'up';

    const selectedElement =
      this.querySelector(
        '[aria-selected="true"]'
      );

    /*
     * Hidden duplicate resources are excluded
     * using offsetParent.
     */
    const allVisibleElements =
      Array.from(
        this.querySelectorAll(
          'li, button.predictive-search__item'
        )
      ).filter(
        (element) =>
          element.offsetParent !== null
      );

    if (!allVisibleElements.length) {
      return;
    }

    if (
      moveUp &&
      !selectedElement
    ) {
      return;
    }

    let selectedElementIndex =
      selectedElement
        ? allVisibleElements.indexOf(
            selectedElement
          )
        : -1;

    /*
     * Selected element may have disappeared
     * after an AJAX refresh.
     */
    if (
      selectedElement &&
      selectedElementIndex === -1
    ) {
      selectedElement.setAttribute(
        'aria-selected',
        'false'
      );

      selectedElementIndex = -1;
    }

    let activeElementIndex = 0;

    if (
      !moveUp &&
      selectedElementIndex !== -1
    ) {
      activeElementIndex =
        selectedElementIndex ===
        allVisibleElements.length - 1
          ? 0
          : selectedElementIndex + 1;
    } else if (
      moveUp &&
      selectedElementIndex !== -1
    ) {
      activeElementIndex =
        selectedElementIndex === 0
          ? allVisibleElements.length - 1
          : selectedElementIndex - 1;
    }

    if (
      activeElementIndex ===
      selectedElementIndex
    ) {
      return;
    }

    const activeElement =
      allVisibleElements[
        activeElementIndex
      ];

    if (!activeElement) {
      return;
    }

    if (this.statusElement) {
      this.statusElement.textContent =
        '';
    }

    activeElement.setAttribute(
      'aria-selected',
      'true'
    );

    if (
      selectedElement &&
      selectedElement !== activeElement
    ) {
      selectedElement.setAttribute(
        'aria-selected',
        'false'
      );
    }

    if (activeElement.id) {
      this.input.setAttribute(
        'aria-activedescendant',
        activeElement.id
      );
    } else {
      this.input.removeAttribute(
        'aria-activedescendant'
      );
    }
  }

  selectOption() {
    const selectedOption =
      this.querySelector(
        '[aria-selected="true"] a, button[aria-selected="true"]'
      );

    if (selectedOption) {
      selectedOption.click();
    }
  }

  getSearchResults(searchTerm) {
    const normalizedTerm =
      searchTerm
        .trim()
        .replace(/\s+/g, ' ');

    if (!normalizedTerm.length) {
      return;
    }

    /*
     * Do not replace spaces with hyphens.
     *
     * "hot wheels"
     * and
     * "hot-wheels"
     *
     * should not share the same cache key.
     */
    const queryKey =
      normalizedTerm.toLowerCase();

    this.setLiveRegionLoadingState();

    /*
     * Cancel the previous request.
     */
    this.abortController.abort();

    this.abortController =
      new AbortController();

    const controller =
      this.abortController;

    const currentRequestId =
      ++this.requestId;

    /*
     * Cached response.
     */
    if (
      Object.prototype.hasOwnProperty.call(
        this.cachedResults,
        queryKey
      )
    ) {
      if (
        this.getQuery().toLowerCase() ===
        normalizedTerm.toLowerCase()
      ) {
        this.renderSearchResults(
          this.cachedResults[
            queryKey
          ]
        );
      }

      return;
    }

    const predictiveSearchUrl =
      window.routes
        ?.predictive_search_url ||
      window.routes
        ?.predictiveSearchUrl ||
      (typeof routes !== 'undefined'
        ? routes.predictive_search_url
        : null);

    if (!predictiveSearchUrl) {
      console.error(
        '[Predictive Search] Predictive search URL is unavailable.'
      );

      this.removeAttribute('loading');

      return;
    }

    const url =
      `${predictiveSearchUrl}` +
      `?q=${encodeURIComponent(
        normalizedTerm
      )}` +
      `&filter.v.availability=1` +
      `&section_id=predictive-search`;

    fetch(url, {
      method: 'GET',

      signal:
        controller.signal,

      headers: {
        Accept: 'text/html',
      },
    })
      .then((response) => {
        if (!response.ok) {
          throw new Error(
            `Predictive search HTTP ${response.status}`
          );
        }

        return response.text();
      })
      .then((text) => {
        /*
         * Ignore stale responses.
         */
        if (
          controller.signal.aborted ||
          currentRequestId !==
            this.requestId
        ) {
          return;
        }

        /*
         * Query may have changed while the
         * request was downloading.
         */
        if (
          this.getQuery()
            .trim()
            .replace(/\s+/g, ' ')
            .toLowerCase() !==
          normalizedTerm.toLowerCase()
        ) {
          return;
        }

        const documentHTML =
          new DOMParser().parseFromString(
            text,
            'text/html'
          );

        const section =
          documentHTML.querySelector(
            '#shopify-section-predictive-search'
          );

        if (!section) {
          throw new Error(
            'Predictive search section was not returned.'
          );
        }

        const resultsMarkup =
          section.innerHTML;

        /*
         * Share cached results between all
         * predictive-search instances.
         */
        document
          .querySelectorAll(
            'predictive-search'
          )
          .forEach(
            (
              predictiveSearchInstance
            ) => {
              predictiveSearchInstance.cachedResults[
                queryKey
              ] = resultsMarkup;

              PredictiveSearch.trimCache(
                predictiveSearchInstance
              );
            }
          );

        this.renderSearchResults(
          resultsMarkup
        );
      })
      .catch((error) => {
        /*
         * AbortError is expected when the user
         * continues typing.
         */
        if (
          error?.name ===
            'AbortError' ||
          error?.code === 20
        ) {
          return;
        }

        /*
         * Do not let an old failed request
         * close newer results.
         */
        if (
          currentRequestId !==
          this.requestId
        ) {
          return;
        }

        this.removeAttribute(
          'loading'
        );

        this.close();

        console.error(
          '[Predictive Search]',
          error
        );
      });
  }

  static trimCache(instance) {
    if (
      !instance?.cachedResults
    ) {
      return;
    }

    const keys =
      Object.keys(
        instance.cachedResults
      );

    const maxEntries =
      instance.maxCacheEntries ||
      40;

    if (
      keys.length <= maxEntries
    ) {
      return;
    }

    const keysToDelete =
      keys.slice(
        0,
        keys.length - maxEntries
      );

    keysToDelete.forEach(
      (key) => {
        delete instance.cachedResults[
          key
        ];
      }
    );
  }

  setLiveRegionLoadingState() {
    this.statusElement =
      this.statusElement ||
      this.querySelector(
        '.predictive-search-status'
      );

    this.loadingText =
      this.loadingText ||
      this.getAttribute(
        'data-loading-text'
      ) ||
      'Loading';

    this.setLiveRegionText(
      this.loadingText
    );

    this.setAttribute(
      'loading',
      'true'
    );
  }

  setLiveRegionText(statusText) {
    if (!this.statusElement) {
      return;
    }

    if (this.liveRegionTimeout) {
      clearTimeout(
        this.liveRegionTimeout
      );
    }

    this.statusElement.setAttribute(
      'aria-hidden',
      'false'
    );

    this.statusElement.textContent =
      statusText || '';

    this.liveRegionTimeout =
      setTimeout(() => {
        if (
          this.statusElement
        ) {
          this.statusElement.setAttribute(
            'aria-hidden',
            'true'
          );
        }
      }, 1000);
  }

  renderSearchResults(
    resultsMarkup
  ) {
    if (
      !this.predictiveSearchResults
    ) {
      console.warn(
        '[Predictive Search] Results container not found.'
      );

      this.removeAttribute(
        'loading'
      );

      return;
    }

    this.predictiveSearchResults.innerHTML =
      resultsMarkup;

    this.setAttribute(
      'results',
      'true'
    );

    this.setLiveRegionResults();

    this.open();
  }

  setLiveRegionResults() {
    this.removeAttribute('loading');

    const countElement =
      this.querySelector(
        '[data-predictive-search-live-region-count-value]'
      );

    const countText =
      countElement?.textContent ||
      '';

    this.setLiveRegionText(
      countText
    );
  }

  getResultsMaxHeight() {
    const header =
      document.querySelector(
        '.section-header'
      );

    const headerBottom =
      header
        ?.getBoundingClientRect()
        ?.bottom || 0;

    const availableHeight =
      window.innerHeight -
      Math.max(
        headerBottom,
        0
      );

    this.resultsMaxHeight =
      Math.max(
        availableHeight,
        0
      );

    return this.resultsMaxHeight;
  }

  open() {
    if (
      !this.predictiveSearchResults ||
      !this.input
    ) {
      return;
    }

    const maxHeight =
      this.resultsMaxHeight ||
      this.getResultsMaxHeight();

    if (
      Number.isFinite(
        maxHeight
      ) &&
      maxHeight > 0
    ) {
      this.predictiveSearchResults.style.maxHeight =
        `${maxHeight}px`;
    } else {
      this.predictiveSearchResults.style.removeProperty(
        'max-height'
      );
    }

    this.setAttribute(
      'open',
      'true'
    );

    this.input.setAttribute(
      'aria-expanded',
      'true'
    );

    this.isOpen = true;
  }

  close(
    clearSearchTerm = false
  ) {
    this.closeResults(
      clearSearchTerm
    );

    this.isOpen = false;
  }

  closeResults(
    clearSearchTerm = false
  ) {
    if (clearSearchTerm) {
      if (this.input) {
        this.input.value = '';
      }

      this.searchTerm = '';

      this.removeAttribute(
        'results'
      );
    }

    const selected =
      this.querySelector(
        '[aria-selected="true"]'
      );

    if (selected) {
      selected.setAttribute(
        'aria-selected',
        'false'
      );
    }

    if (this.input) {
      this.input.removeAttribute(
        'aria-activedescendant'
      );

      this.input.setAttribute(
        'aria-expanded',
        'false'
      );
    }

    this.removeAttribute(
      'loading'
    );

    this.removeAttribute(
      'open'
    );

    this.resultsMaxHeight = false;

    if (
      this.predictiveSearchResults
    ) {
      this.predictiveSearchResults.style.removeProperty(
        'max-height'
      );
    }
  }
}

if (
  !customElements.get(
    'predictive-search'
  )
) {
  customElements.define(
    'predictive-search',
    PredictiveSearch
  );
}